export const FUNNEL_LATEST_ID = 'latest'
export const FUNNEL_LATEST_NAME = 'roastdating'
export const FUNNEL_LATEST_VARIANT = 'latest'

export const FUNNEL_LATEST_ANSWER_KEYS = [
  'gender',
  'problem',
  'orientation',
  'age',
  'goal',
  'frustration',
  'holding_back',
  'help_level',
  'budget',
  'motivation',
] as const

export const FUNNEL_LATEST_STEPS = {
  gender: { stepNumber: 1, stepName: 'gender', stepType: 'question' },
  problem: { stepNumber: 2, stepName: 'problem', stepType: 'question' },
  problem_reward: {
    stepNumber: 3,
    stepName: 'problem_reward',
    stepType: 'reward',
  },
  orientation: { stepNumber: 4, stepName: 'orientation', stepType: 'question' },
  age: { stepNumber: 5, stepName: 'age', stepType: 'question' },
  info_reward: {
    stepNumber: 6,
    stepName: 'info_reward',
    stepType: 'reward',
  },
  goal: { stepNumber: 7, stepName: 'goal', stepType: 'question' },
  goal_reward: {
    stepNumber: 8,
    stepName: 'goal_reward',
    stepType: 'reward',
  },
  frustration: {
    stepNumber: 9,
    stepName: 'frustration',
    stepType: 'question',
  },
  frustration_reward: {
    stepNumber: 10,
    stepName: 'frustration_reward',
    stepType: 'reward',
  },
  holding_back: {
    stepNumber: 11,
    stepName: 'holding_back',
    stepType: 'question',
  },
  holding_back_reward: {
    stepNumber: 12,
    stepName: 'holding_back_reward',
    stepType: 'reward',
  },
  help_level: {
    stepNumber: 13,
    stepName: 'help_level',
    stepType: 'question',
  },
  budget: { stepNumber: 14, stepName: 'budget', stepType: 'question' },
  budget_reward: {
    stepNumber: 15,
    stepName: 'budget_reward',
    stepType: 'reward',
  },
  motivation: { stepNumber: 16, stepName: 'motivation', stepType: 'question' },
  motivation_reward: {
    stepNumber: 17,
    stepName: 'motivation_reward',
    stepType: 'reward',
  },
  upload: { stepNumber: 18, stepName: 'upload', stepType: 'upload' },
  photos: { stepNumber: 19, stepName: 'photos', stepType: 'review' },
  analyze: { stepNumber: 20, stepName: 'analyze', stepType: 'analysis' },
  email: { stepNumber: 21, stepName: 'email', stepType: 'contact' },
  paywall: { stepNumber: 22, stepName: 'paywall', stepType: 'offer' },
  success: { stepNumber: 23, stepName: 'success', stepType: 'success' },
} as const

export type FunnelLatestStepKey = keyof typeof FUNNEL_LATEST_STEPS

type FunnelLatestAnswers = Partial<
  Record<(typeof FUNNEL_LATEST_ANSWER_KEYS)[number], string | string[]>
>

export function sanitizeFunnelLatestAnswers(value: unknown): FunnelLatestAnswers {
  if (!value || typeof value !== 'object') {
    return {}
  }

  const answers: FunnelLatestAnswers = {}

  for (const key of FUNNEL_LATEST_ANSWER_KEYS) {
    const answer = (value as Record<string, unknown>)[key]

    if (typeof answer === 'string' && answer.length > 0) {
      answers[key] = answer
    }

    if (Array.isArray(answer) && answer.every((item) => typeof item === 'string')) {
      answers[key] = answer
    }
  }

  return answers
}

export function getFunnelLatestStepProperties(stepKey: FunnelLatestStepKey) {
  const step = FUNNEL_LATEST_STEPS[stepKey]

  return {
    funnel_id: FUNNEL_LATEST_ID,
    funnel_name: FUNNEL_LATEST_NAME,
    funnel_variant: FUNNEL_LATEST_VARIANT,
    step_key: stepKey,
    step_name: step.stepName,
    step_number: step.stepNumber,
    step_type: step.stepType,
  }
}

export function getFunnelLatestQuizProperties(stepKey: FunnelLatestStepKey) {
  const step = FUNNEL_LATEST_STEPS[stepKey]

  return {
    ...getFunnelLatestStepProperties(stepKey),
    question_key: stepKey,
    response_type: step.stepName === 'holding_back' ? 'multi_select' : 'single_select',
  }
}

export function getFunnelLatestPersonProperties(value: unknown) {
  const answers = sanitizeFunnelLatestAnswers(value)

  if (Object.keys(answers).length === 0) {
    return {}
  }

  const properties: Record<string, string | number | string[]> = {
    funnel_latest_variant: FUNNEL_LATEST_VARIANT,
    funnel_latest_answered_questions_count: Object.keys(answers).length,
  }

  for (const [key, answer] of Object.entries(answers)) {
    if (Array.isArray(answer)) {
      properties[`funnel_latest_${key}`] = answer.join('|')
      properties[`funnel_latest_${key}_values`] = answer
      properties[`funnel_latest_${key}_count`] = answer.length
      continue
    }

    properties[`funnel_latest_${key}`] = answer
  }

  return properties
}
